import type { HTMLAttributes, ReactNode } from "react";
import { tv, type VariantProps } from "tailwind-variants";

import { cn } from "../../lib/cn";
import "./ShimmerLoading.css";

const shimmerLoadingVariants = tv({
    base: "shimmer-loading relative overflow-hidden [@container-type:size]",
    variants: {
        variant: {
            default: "bg-component-shimmer-loading-gradient-from",
            light: [
                "shimmer-loading--light",
                "bg-component-shimmer-loading-light-gradient-from",
            ],
        },
        fill: {
            solid: "",
            none: "shimmer-loading--no-fill bg-transparent",
        },
    },
    defaultVariants: {
        variant: "default",
        fill: "solid",
    },
});

type ShimmerLoadingProps = HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof shimmerLoadingVariants> & {
        /** Fades the moving highlight out, leaving the static fill. */
        showsSwoosh?: boolean;
    };

export function ShimmerLoading({
    className,
    variant = "default",
    fill = "solid",
    showsSwoosh = true,
    ...props
}: ShimmerLoadingProps) {
    return (
        <div
            {...props}
            aria-hidden
            className={cn(shimmerLoadingVariants({ variant, fill }), className)}
        >
            <div
                className={cn(
                    "shimmer-loading__swoosh absolute top-1/2 left-1/2 size-[max(100cqw,100cqh)] -translate-x-1/2 -translate-y-1/2 transition-opacity duration-400 ease-out",
                    !showsSwoosh && "opacity-0",
                )}
            />
        </div>
    );
}

type ShimmerLoadingOverlayProps = {
    isReady: boolean;
    className?: string;
    variant?: VariantProps<typeof shimmerLoadingVariants>["variant"];
    children: ReactNode;
};

export function ShimmerLoadingOverlay({
    isReady,
    className,
    variant = "default",
    children,
}: ShimmerLoadingOverlayProps) {
    return (
        <div className={cn("relative overflow-hidden", className)}>
            <ShimmerLoading
                variant={variant}
                className={cn(
                    "absolute inset-0 transition-opacity duration-[350ms] ease-in-out",
                    isReady ? "pointer-events-none opacity-0" : "opacity-100",
                )}
            />
            <div
                className={cn(
                    "size-full transition-opacity duration-[350ms] ease-in-out",
                    isReady
                        ? "pointer-events-auto opacity-100"
                        : "pointer-events-none opacity-0",
                )}
                aria-hidden={!isReady}
            >
                {children}
            </div>
        </div>
    );
}
